import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Wifi, Wind, Coffee, Droplets, MapPin, Map, ChevronDown, ChevronUp } from 'lucide-react';
import { rooms, amenities, testimonials } from '../data/mockData';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0" aria-label="Google">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-orange-50 transition-colors"
        aria-expanded={isOpen}
      >
        <h3 className="text-lg font-semibold text-gray-800 pr-4">{question}</h3>
        {isOpen ? <ChevronUp className="text-orange-600 flex-shrink-0" size={20} /> : <ChevronDown className="text-orange-600 flex-shrink-0" size={20} />}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 text-gray-600 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
};

const Home = () => {
  const [roomGalleryImages, setRoomGalleryImages] = useState({});

  useEffect(() => {
    const fetchRoomGalleryImages = async () => {
      const categories = ['deluxe', 'premium', 'ultra-large'];
      const imageMap = {};
      try {
        await Promise.all(
          categories.map(async (category) => {
            const response = await axios.get(`${BACKEND_URL}/api/gallery?category=${category}`);
            if (response.data && response.data.length > 0) {
              imageMap[category] = response.data[0].url;
            }
          })
        );
        setRoomGalleryImages(imageMap);
      } catch (error) {
        // Fall back to static images on error
      }
    };
    fetchRoomGalleryImages();
  }, []);

  const roomTypeToCategory = { 'Deluxe': 'deluxe', 'Standard': 'premium', 'Ultra-Large': 'ultra-large' };

  const getRoomImage = (room) => {
    const category = roomTypeToCategory[room.type];
    return (category && roomGalleryImages[category]) ? roomGalleryImages[category] : room.images[0];
  };

  const faqs = [
    {
      question: "Where is Ved Homestays located in Varanasi?",
      answer: "Ved Homestays is located near Kashi Vishwanath Temple on Ganga Ghat Road, Varanasi, UP 221001. We are just a 5-minute walk from the famous Kashi Vishwanath Temple and Dashashwamedh Ghat where the Ganga Aarti takes place every evening."
    },
    {
      question: "What is the room price at Ved Homestays Varanasi?",
      answer: <span>Our room prices are very reasonable and designed to suit every budget — whether you're a solo traveller, a couple, or a large family group visiting Banaras. Every room comes with AC, free WiFi, attached bathroom, and 24/7 hot water. <Link to="/rooms" className="text-orange-600 underline font-medium hover:text-orange-800">Check room availability and pricing here →</Link></span>
    },
    {
      question: "Is Ved Homestays near Kashi Vishwanath Temple?",
      answer: "Yes! Ved Homestays is one of the closest accommodations to Kashi Vishwanath Temple — just a 5-minute walk. You can easily visit the temple for morning and evening darshan without any transportation."
    },
    {
      question: "How do I book a room at Ved Homestays?",
      answer: "You can book by calling +91 9984397001, messaging on WhatsApp, emailing vedhomestays@gmail.com, or using our website booking form. We also accept bookings through Airbnb, MakeMyTrip, and Goibibo."
    },
    {
      question: "Does Ved Homestays offer group accommodation in Varanasi?",
      answer: <span>Yes! Our Ultra-Large Room can accommodate up to 10 guests with 6 single beds at very reasonable rates. Extra mattresses are also available. It's perfect for families, friend groups, and pilgrim groups visiting Varanasi. <Link to="/rooms" className="text-orange-600 underline font-medium hover:text-orange-800">See group room details →</Link></span>
    },
    {
      question: "What amenities are available at Ved Homestays?",
      answer: "All rooms include Air Conditioning, free high-speed WiFi, attached bathroom with 24/7 hot water, room service, clean linens, and comfortable beds. We also arrange Varanasi tours, airport/station pickup, and provide local food recommendations."
    },
    {
      question: "How far is Ved Homestays from Varanasi Railway Station?",
      answer: "Ved Homestays is approximately 6 km from Varanasi Junction (Cantt) Railway Station (about 20 minutes by auto) and about 30 km from Lal Bahadur Shastri International Airport (about 45-60 minutes by cab). We can arrange pickup for our guests."
    },
    {
      question: "Why choose Ved Homestays over a hotel in Varanasi?",
      answer: "At Ved Homestays, you don't just get a room — you experience Banaras like a local. Wake up to temple bells, stroll through ancient galis to the ghats for morning aarti, get insider tips on the best street food from lassi to chaat, and feel the warmth of a Banarasi family welcoming you home. With 5-star Google ratings, a prime location steps from Kashi Vishwanath Temple, and all modern amenities, we deliver authentic local living that no hotel can match."
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Best Homestay in Varanasi Near Kashi Vishwanath Temple"
        description="Ved Homestays — #1 rated homestay in Varanasi near Kashi Vishwanath Temple & Ganga Aarti Ghats. Affordable rooms from ₹1200/night. Book hotel in Varanasi for authentic Banarasi experience. Live in Banaras like a local!"
        keywords="hotel in varanasi, hotels in varanasi, varanasi hotel booking, best hotel in varanasi, hotel near kashi vishwanath temple, homestay in varanasi, varanasi accommodation, hotel near ganga ghat, budget hotel varanasi, varanasi guesthouse, cheap hotel in varanasi, family hotel varanasi, group accommodation varanasi, lodge in varanasi, guest house varanasi, kashi hotel, banaras hotel, hotel near dashashwamedh ghat, varanasi stay, where to stay in varanasi, best homestay varanasi, varanasi hotel near temple, dharamshala varanasi, hotel room varanasi, varanasi hotel room price"
        url="https://www.vedhomestays.com/"
        breadcrumbs={[
          { name: "Home", url: "https://www.vedhomestays.com/" }
        ]}
      />
      <StructuredData />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1706186839147-0d708602587b"
            alt="Varanasi Ganga Ghat - Ved Homestays is located near these beautiful ghats"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
            Best Homestay in Varanasi
          </h1>
          <p className="text-xl md:text-3xl mb-4 text-orange-300 font-medium">
            Live in Banaras Like a Banarasi
          </p>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-gray-200">
            Ved Homestays — Hotel near Kashi Vishwanath Temple & Ganga Aarti Ghats. Rooms from ₹1200/night.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/rooms"
              className="px-8 py-4 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-all hover:scale-105 inline-flex items-center justify-center"
            >
              Check Room Availability <ArrowRight className="ml-2" size={20} />
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 bg-white text-orange-600 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:scale-105"
            >
              Book Now — Best Price Guaranteed
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-white rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Welcome Section — keyword-rich intro */}
      <section className="py-20 bg-gradient-to-b from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Your Home in the Heart of Varanasi
            </h2>
            <p className="text-lg text-gray-600 mb-4 leading-relaxed">
              Looking for the <strong>best hotel in Varanasi near Kashi Vishwanath Temple</strong>? Ved Homestays is your perfect choice.
              Nestled in the spiritual capital of India, we offer an authentic Banarasi experience that no chain hotel can match.
            </p>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Located just a <strong>5-minute walk from Kashi Vishwanath Temple</strong> and the mesmerizing <strong>Ganga Aarti at Dashashwamedh Ghat</strong>,
              Ved Homestays provides comfortable <strong>accommodation in Varanasi</strong> with warm hospitality that makes you feel like family.
              Whether you're seeking a <strong>budget hotel in Varanasi</strong>, a <strong>family homestay near Ganga ghats</strong>, or
              <strong> group accommodation in Kashi</strong> — we have the perfect room for you, starting from just <strong>₹1200 per night</strong>.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-600 mb-2">4</div>
                <div className="text-gray-600">Rooms Available</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-600 mb-2">20+</div>
                <div className="text-gray-600">Guest Capacity</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-600 mb-2">5★</div>
                <div className="text-gray-600">Google Rating</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-600 mb-2">5 min</div>
                <div className="text-gray-600">to Kashi Vishwanath Temple</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-4">
            Hotel Amenities at Ved Homestays Varanasi
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Enjoy modern facilities with traditional Banarasi hospitality — everything you need for a comfortable stay in Varanasi
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {amenities.map((amenity, index) => {
              const IconComponent = {
                Wifi,
                Wind,
                Coffee,
                Droplets,
                MapPin,
                Map
              }[amenity.icon];

              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl hover:shadow-lg transition-all hover:-translate-y-1"
                >
                  <div className="w-14 h-14 bg-orange-600 rounded-lg flex items-center justify-center mb-4">
                    <IconComponent className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{amenity.title}</h3>
                  <p className="text-gray-600">{amenity.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Rooms */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-4">
            Hotel Rooms in Varanasi
          </h2>
          <p className="text-center text-gray-600 mb-12">
            Affordable rooms near Kashi Vishwanath Temple for couples, families, and groups
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {rooms.map((room) => (
              <article
                key={room.id}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={getRoomImage(room)}
                    alt={`${room.type} Room at Ved Homestays Varanasi — ${room.bedType}, near Kashi Vishwanath Temple`}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                  />
                  {room.available && (
                    <span className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                      Available
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{room.type} Room</h3>
                  <p className="text-sm text-gray-600 mb-3">{room.bedType} • Up to {room.maxOccupancy} guests</p>
                  <p className="text-sm text-gray-500 mb-4">{room.description}</p>
                  <Link
                    to="/rooms"
                    className="block w-full text-center px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors"
                  >
                    Check Availability & Pricing
                  </Link>
                </div>
              </article>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/rooms"
              className="inline-flex items-center px-8 py-3 bg-gray-800 text-white rounded-lg font-semibold hover:bg-gray-900 transition-colors"
            >
              View All Rooms & Book <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Nearby Attractions — targets "hotel near X" searches */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-4">
            Nearby Attractions — Walking Distance from Ved Homestays
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Stay at the best location in Varanasi — everything is within walking distance
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl text-center">
              <div className="text-3xl mb-3">🕉️</div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Kashi Vishwanath Temple</h3>
              <p className="text-sm text-gray-600 mb-2">5 min walk</p>
              <p className="text-xs text-gray-500">One of the 12 Jyotirlingas, the holiest Shiva temple</p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl text-center">
              <div className="text-3xl mb-3">🪔</div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Dashashwamedh Ghat</h3>
              <p className="text-sm text-gray-600 mb-2">5 min walk</p>
              <p className="text-xs text-gray-500">Famous for the spectacular evening Ganga Aarti ceremony</p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl text-center">
              <div className="text-3xl mb-3">🔥</div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Manikarnika Ghat</h3>
              <p className="text-sm text-gray-600 mb-2">10 min walk</p>
              <p className="text-xs text-gray-500">Sacred cremation ghat on the banks of the Ganges</p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl text-center">
              <div className="text-3xl mb-3">🛕</div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">BHU & Assi Ghat</h3>
              <p className="text-sm text-gray-600 mb-2">Auto/Rickshaw</p>
              <p className="text-xs text-gray-500">Popular ghat for morning yoga and cultural activities</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-orange-50 via-amber-50 to-orange-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-4">
            Guest Reviews — Ved Homestays Varanasi
          </h2>
          <div className="flex items-center justify-center gap-2 mb-4">
            <GoogleIcon />
            <span className="text-gray-600 font-medium">Google Reviews</span>
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} className="text-yellow-500 fill-yellow-500" />
              ))}
            </div>
            <span className="text-gray-600 font-medium">5.0/5</span>
          </div>
          <p className="text-center text-gray-600 mb-12">
            Hear from travelers who experienced authentic Varanasi with us
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <article
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} className="text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <GoogleIcon />
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t pt-4">
                  <div className="font-semibold text-gray-800">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                  <div className="text-xs text-gray-400 mt-1">{testimonial.date}</div>
                </div>
              </article>
            ))}
          </div>
          <div className="text-center mt-10">
            <a
              href="https://www.google.com/maps/search/Ved+Homestays+Varanasi"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-gray-300 rounded-full text-gray-700 font-medium hover:border-orange-400 hover:text-orange-600 transition-colors"
            >
              <GoogleIcon />
              See all reviews on Google
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Ved Homestays — SEO content block */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-8">
              Why Ved Homestays is the Best Hotel in Varanasi
            </h2>
            <div className="prose prose-lg text-gray-600 leading-relaxed space-y-4">
              <p>
                Planning a trip to <strong>Varanasi</strong>? Whether you're visiting for a spiritual pilgrimage to <strong>Kashi Vishwanath Temple</strong>,
                witnessing the iconic <strong>Ganga Aarti at Dashashwamedh Ghat</strong>, or exploring the ancient lanes of <strong>Banaras</strong>,
                where you stay makes all the difference.
              </p>
              <p>
                <strong>Ved Homestays</strong> stands apart from typical <strong>hotels in Varanasi</strong> by offering
                what they cannot — a genuine, local experience. Our homestay is run by a Banarasi family who knows every hidden gem, every 
                must-try food stall, every secret ghat, and every story this 5000-year-old city holds.
              </p>
              <p>
                Unlike impersonal <strong>budget hotels in Varanasi</strong>, we provide personalized hospitality. Need a guide for 
                <strong> Sarnath</strong>? Want to know the best time for <strong>Ganga Aarti</strong>? Looking for the tastiest 
                <strong> Banarasi paan</strong> or <strong>kachori</strong>? We'll make it happen.
              </p>
              <p>
                With <strong>rooms starting from just ₹1200 per night</strong>, all including <strong>AC, free WiFi, attached bathroom</strong>, 
                and <strong>24/7 hot water</strong>, Ved Homestays delivers the best value for a <strong>hotel near Kashi Vishwanath Temple</strong>. 
                Whether you're a solo traveler, a couple, a family, or a group of up to 10 — we have the perfect room.
              </p>
              <p className="text-center">
                <Link to="/contact" className="text-orange-600 font-bold hover:underline">
                  Book your stay at Ved Homestays →
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section — targets "People Also Ask" in Google */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-4">
              Frequently Asked Questions — Ved Homestays Varanasi
            </h2>
            <p className="text-center text-gray-600 mb-12">
              Everything you need to know about staying at our hotel near Kashi Vishwanath Temple
            </p>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <FAQItem key={index} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-amber-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Book Your Varanasi Hotel — Best Price Guaranteed
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Book directly with Ved Homestays for the best rates. No middlemen, no extra charges — just authentic Banarasi hospitality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-10 py-4 bg-white text-orange-600 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105"
            >
              Book Your Stay Now <ArrowRight className="ml-2" size={24} />
            </Link>
            <a
              href="https://wa.me/919984397001?text=Hi%2C%20I%20want%20to%20book%20a%20room%20at%20Ved%20Homestays%20Varanasi"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-10 py-4 border-2 border-white text-white rounded-lg font-bold text-lg hover:bg-white/10 transition-all hover:scale-105"
            >
              WhatsApp Us — +91 9984397001
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
